/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps */
import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTheme } from '../../context/ThemeContext';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { useTranslation } from 'react-i18next';
import axiosClient from '../../api/axiosClient';
import { widgetStyles, animations } from '../../utils/designTokens';
import { CircularProgress } from '@mui/material';
import PortalModal from '../../components/shared/PortalModal';
import ProfileAvatar from '../../components/shared/ProfileAvatar';
import ProfileImageUpload from '../../components/shared/ProfileImageUpload';

// Brand accent color
const ACCENT = '#7d6633';

// Glass morphism Card component
const Card = ({ children, className = '' }) => (
  <div
    className={
      'rounded-2xl shadow-lg border border-white/20 dark:border-white/10 bg-white/70 dark:bg-zinc-900/60 backdrop-blur-xl ' +
      '' + className
    }
  >
    {children}
  </div>
);

// Card Header component
const CardHeader = ({ title, icon, action, subtitle }) => (
  <div className="flex items-center justify-between p-3 md:p-4 lg:p-5 border-b border-white/10 dark:border-white/10">
    <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
      <div className="h-8 w-8 md:h-10 md:w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center flex-shrink-0" style={{ '--accent': ACCENT }}>
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <h3 className="text-sm md:text-base font-semibold text-zinc-900 dark:text-zinc-100">{title}</h3>
        {subtitle && (
          <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-0.5 hidden md:block">{subtitle}</p>
        )}
      </div>
    </div>
    {action}
  </div>
);

// Icons using SVG for consistency with client page style
const Icons = {
  Plus: () => (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  ),
  Search: () => (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  ),
  Edit: () => (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  ),
  Archive: () => (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 8l4 4-4 4m6-8l4 4-4 4" />
    </svg>
  ),
  Restore: () => (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  ),
  Eye: () => (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  ),
  Loading: () => (
    <svg className="animate-spin h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  ),
  User: () => (
    <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  Delete: () => (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  ),
  HairAnalysis: () => (
    <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
    </svg>
  )
};

// Create Client Modal
const CreateClientModal = ({ isOpen, onClose, onSuccess, isDark, barbers, user }) => {
  const { t } = useTranslation('admin');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferred_barber_id: ''
  });
  const [pendingImageFile, setPendingImageFile] = useState(null);
  const [pendingPreviewUrl, setPendingPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const resolvedShopId = (() => {
    const urlShopId =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('shopId')
        : null;
    return user?.shop_id || urlShopId || null;
  })();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        ...formData,
        preferred_barber_id: formData.preferred_barber_id || null
      };

      if (!payload.email.trim()) delete payload.email;
      if (!payload.phone.trim()) delete payload.phone;

      const response = await axiosClient.post('/admin/clients', payload);

      if (response.data.ok) {
        let createdClient = response.data.data;

        // Upload staged photo if any
        if (pendingImageFile && createdClient?.user_id && resolvedShopId) {
          try {
            const fd = new FormData();
            fd.append('image', pendingImageFile);
            const imgRes = await axiosClient.post(
              `/admin/profile-images/${createdClient.user_id}?shop_id=${resolvedShopId}`,
              fd,
              { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            if (imgRes.data?.ok) {
              createdClient = { ...createdClient, avatar_url: imgRes.data.data.avatar_url };
            }
          } catch (imgErr) {
            console.warn('[CreateClient] profile image upload failed:', imgErr);
          }
        }

        onSuccess(createdClient);
        onClose();
        setFormData({ name: '', email: '', phone: '', preferred_barber_id: '' });
        setPendingImageFile(null);
        setPendingPreviewUrl(null);
      }
    } catch (err) {
      setError(err.response?.data?.error || t('admin.create_client'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileSelected = (file) => {
    setPendingImageFile(file);
    if (file) {
      const url = URL.createObjectURL(file);
      setPendingPreviewUrl(url);
    } else {
      setPendingPreviewUrl(null);
    }
  };

  if (!isOpen) return null;

  return (
    <PortalModal open={isOpen}>
      <div className="hc-modal-overlay bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center">
        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          className="hc-modal-panel p-4 md:p-6 rounded-2xl max-w-md w-full my-8 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border border-white/20 dark:border-white/10 shadow-2xl"
        >
        <div className="flex items-center mb-4">
          <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center mr-3" style={{ '--accent': ACCENT }}>
            <Icons.Plus />
          </div>
          <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            {t('admin.create_client')}
          </h3>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Profile photo (staged) */}
          <div className="mb-5 flex justify-center">
            <ProfileImageUpload
              name={formData.name}
              userId={null}
              shopId={resolvedShopId}
              currentAvatarUrl={pendingPreviewUrl}
              onFileSelected={handleFileSelected}
              onRemoveSuccess={() => handleFileSelected(null)}
              size="xl"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              {t('admin.client_form_name')} *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              minLength={2}
              maxLength={80}
              className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
              style={{ '--accent': ACCENT }}
              placeholder={t('admin.client_name_placeholder')}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              {t('admin.client_email_label')} ({t('admin.no_preference').toLowerCase()})
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
              style={{ '--accent': ACCENT }}
              placeholder={t('admin.client_email_placeholder')}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              {t('admin.client_phone_label')} ({t('admin.no_preference').toLowerCase()})
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
              style={{ '--accent': ACCENT }}
              placeholder={t('admin.client_phone_placeholder')}
              pattern="^\+[1-9]\d{1,14}$"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              {t('admin.barber_label')} ({t('admin.no_preference').toLowerCase()})
            </label>
            <select
              name="preferred_barber_id"
              value={formData.preferred_barber_id}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
              style={{ '--accent': ACCENT }}
            >
              <option value="">{t('admin.no_preference')}</option>
              {barbers.map(barber => (
                <option key={barber.id} value={barber.id}>
                  {barber.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end space-x-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
            >
              {t('admin.cancel')}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded-xl text-white font-medium flex items-center gap-2 transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: ACCENT }}
            >
              {loading ? <Icons.Loading /> : <Icons.Plus />}
              <span>{t('admin.create_client')}</span>
            </motion.button>
          </div>
        </form>
        </motion.div>
      </div>
    </PortalModal>
  );
};

// Edit Client Modal
const EditClientModal = ({ isOpen, onClose, onSuccess, isDark, client, barbers, user }) => {
  const { t } = useTranslation('admin');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferred_barber_id: '',
    avatar_url: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const resolvedShopId = (() => {
    const urlShopId =
      typeof window !== 'undefined'
        ? new URLSearchParams(window.location.search).get('shopId')
        : null;
    return user?.shop_id || urlShopId || null;
  })();

  useEffect(() => {
    if (client) {
      setFormData({
        name: client.name || '',
        email: client.email || '',
        phone: client.phone || '',
        preferred_barber_id: client.preferred_barber_id || '',
        avatar_url: client.avatar_url || ''
      });
    }
  }, [client]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const payload = {
        ...formData,
        preferred_barber_id: formData.preferred_barber_id || null
      };

      // Remove empty email to avoid validation issues
      if (!payload.email.trim()) {
        payload.email = null;
      }

      // Remove empty phone to avoid validation issues
      if (!payload.phone.trim()) {
        payload.phone = null;
      }

      const response = await axiosClient.patch(`/admin/clients/${client.id}`, payload);

      if (response.data.ok) {
        onSuccess(response.data.data);
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.error || t('admin.update_client'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  if (!isOpen || !client) return null;

  return (
    <PortalModal open={isOpen && !!client}>
      <div className="hc-modal-overlay bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center">
        <motion.div 
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="hc-modal-panel p-4 md:p-6 rounded-2xl max-w-md w-full my-8 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border border-white/20 dark:border-white/10 shadow-2xl"
      >
        <div className="flex items-center mb-4">
          <div className="h-10 w-10 rounded-xl bg-[--accent]/10 flex items-center justify-center mr-3" style={{ '--accent': ACCENT }}>
            <Icons.Edit />
          </div>
          <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">
            {t('admin.update_client')}
          </h3>
        </div>

        {error && (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl mb-4">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          {/* Profile photo */}
          <div className="mb-5 flex justify-center">
            <ProfileImageUpload
              name={formData.name}
              userId={client?.user_id}
              shopId={resolvedShopId}
              currentAvatarUrl={formData.avatar_url}
              onUploadSuccess={(newUrl) =>
                setFormData((prev) => ({ ...prev, avatar_url: newUrl }))
              }
              onRemoveSuccess={() =>
                setFormData((prev) => ({ ...prev, avatar_url: '' }))
              }
              size="xl"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              {t('admin.client_form_name')} *
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              minLength={2}
              maxLength={80}
              className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
              style={{ '--accent': ACCENT }}
              placeholder={t('admin.client_name_placeholder')}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              {t('admin.client_email_label')} ({t('admin.no_preference').toLowerCase()})
            </label>
            <input
              type="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
              style={{ '--accent': ACCENT }}
              placeholder={t('admin.client_email_placeholder')}
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              {t('admin.client_phone_label')} ({t('admin.no_preference').toLowerCase()})
            </label>
            <input
              type="tel"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
              style={{ '--accent': ACCENT }}
              placeholder={t('admin.client_phone_placeholder')}
              pattern="^\+[1-9]\d{1,14}$"
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">
              {t('admin.barber_label')} ({t('admin.no_preference').toLowerCase()})
            </label>
            <select
              name="preferred_barber_id"
              value={formData.preferred_barber_id}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
              style={{ '--accent': ACCENT }}
            >
              <option value="">{t('admin.no_preference')}</option>
              {barbers.map(barber => (
                <option key={barber.id} value={barber.id}>
                  {barber.name}
                </option>
              ))}
            </select>
          </div>

          <div className="flex justify-end space-x-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
            >
              {t('admin.cancel')}
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              type="submit"
              disabled={loading}
              className="px-4 py-2 rounded-xl text-white font-medium flex items-center gap-2 transition-all duration-200 hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: ACCENT }}
            >
              {loading ? <Icons.Loading /> : <Icons.Edit />}
              <span>{t('admin.update_client')}</span>
            </motion.button>
          </div>
        </form>
        </motion.div>
      </div>
    </PortalModal>
  );
};

const Clients = () => {
  const { isDark } = useTheme();
  const { user } = useAdminAuth();
  const { t, i18n } = useTranslation('admin');
  const location = useLocation();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalClients, setTotalClients] = useState(0);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmData, setConfirmData] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [actionLoading, setActionLoading] = useState({});
  const [selectedClients, setSelectedClients] = useState(new Set());
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [focusClientId, setFocusClientId] = useState(null);

  const pageSize = 20;

  // Load clients with current filters
  const loadClients = useCallback(async () => {
    setLoading(true);
    try {
      const urlShopId =
        typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('shopId') : null;
      const resolvedShopId = user?.shop_id || urlShopId || null;
      if (!resolvedShopId) {
        setErrorMessage(t('admin.missing_shop_id'));
        setShowErrorModal(true);
        return;
      }
      const params = new URLSearchParams({
        page: currentPage.toString(),
        pageSize: pageSize.toString(),
        shop_id: resolvedShopId,
        q: searchQuery,
        status: statusFilter
      });

      const response = await axiosClient.get(`/admin/clients?${params}`);
      
      if (response.data.ok) {
        setClients(response.data.data);
        setTotalPages(response.data.pagination.totalPages);
        setTotalClients(response.data.pagination.total);
      }
    } catch (error) {
      console.error('Error loading clients:', error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, searchQuery, statusFilter]);

  // Load barbers for the preferred barber dropdown
  const loadBarbers = useCallback(async () => {
    try {
      const urlShopId =
        typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('shopId') : null;
      const resolvedShopId = user?.shop_id || urlShopId || null;
      if (!resolvedShopId) return;
      const params = new URLSearchParams({
        page: '1',
        pageSize: '100',
        shop_id: resolvedShopId,
        status: 'active'
      });

      const response = await axiosClient.get(`/admin/barbers?${params}`);
      
      if (response.data.ok) {
        setBarbers(response.data.data);
      }
    } catch (error) {
      console.error('Error loading barbers:', error);
    }
  }, []);

  useEffect(() => {
    loadClients();
  }, [loadClients]);

  // Accept deep-links from global search: ?q=...&focus_client_id=123
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const q = params.get('q') || '';
    const focusId = params.get('focus_client_id');

    if (q && q !== searchQuery) {
      setSearchQuery(q);
    }
    if (focusId) {
      const n = Number(focusId);
      if (Number.isFinite(n)) setFocusClientId(n);
    }
  }, [location.search]);  

  // After clients render, scroll & highlight the focused client
  useEffect(() => {
    if (!focusClientId) return;
    if (!clients || clients.length === 0) return;
    const row = document.getElementById(`client-row-${focusClientId}`);
    if (row) {
      row.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const focusTimer = setTimeout(() => setFocusClientId(null), 4000);
      return () => clearTimeout(focusTimer);
    }
  }, [focusClientId, clients]);

  useEffect(() => {
    loadBarbers();
  }, [loadBarbers]);

  // Reset to first page when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, statusFilter]);

  const handleCreateSuccess = (newClient) => {
    setClients(prev => [newClient, ...prev]);
    setTotalClients(prev => prev + 1);
  };

  const handleEditSuccess = (updatedClient) => {
    setClients(prev => prev.map(client => 
      client.id === updatedClient.id ? updatedClient : client
    ));
  };

  const handleArchive = async (clientId) => {
    setActionLoading(prev => ({ ...prev, [clientId]: true }));
    try {
      const response = await axiosClient.patch(`/admin/clients/${clientId}/archive`);
      
      if (response.data.ok) {
        // Remove from current list if showing active, or reload if showing all
        if (statusFilter === 'active') {
          setClients(prev => prev.filter(client => client.id !== clientId));
          setTotalClients(prev => prev - 1);
        } else {
          loadClients(); // Reload to show updated status
        }
      }
    } catch (error) {
      console.error('Error archiving client:', error);
      setErrorMessage(t('admin.client_failed_to_archive'));
      setShowErrorModal(true);
    } finally {
      setActionLoading(prev => ({ ...prev, [clientId]: false }));
    }
  };

  const handleRestore = async (clientId) => {
    setActionLoading(prev => ({ ...prev, [clientId]: true }));
    try {
      const response = await axiosClient.patch(`/admin/clients/${clientId}/restore`);
      
      if (response.data.ok) {
        // Remove from current list if showing archived, or reload if showing all
        if (statusFilter === 'archived') {
          setClients(prev => prev.filter(client => client.id !== clientId));
          setTotalClients(prev => prev - 1);
        } else {
          loadClients(); // Reload to show updated status
        }
      }
    } catch (error) {
      console.error('Error restoring client:', error);
      setErrorMessage(t('admin.client_failed_to_restore'));
      setShowErrorModal(true);
    } finally {
      setActionLoading(prev => ({ ...prev, [clientId]: false }));
    }
  };

  const handleDelete = async (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setConfirmData({
      type: 'delete',
      title: t('admin.delete_client'),
      message: t('admin.delete_client_confirm', { name: client?.name || '' }),
      details: [
        `${t('admin.client_form_name')}: ${client?.name || t('admin.unknown')}`,
        `${t('admin.client_email_label')}: ${client?.email || t('admin.no_email')}`
      ],
      warning: t('admin.delete_client_warning'),
      onConfirm: () => performDelete(clientId)
    });
    setShowConfirmModal(true);
  };

  const performDelete = async (clientId) => {

    setActionLoading(prev => ({ ...prev, [clientId]: true }));
    try {
      const urlShopId =
        typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('shopId') : null;
      const resolvedShopId = user?.shop_id || urlShopId || null;
      if (!resolvedShopId) {
        setErrorMessage(t('admin.missing_shop_id'));
        setShowErrorModal(true);
        return;
      }
      const response = await axiosClient.delete(`/admin/clients/${clientId}?shop_id=${resolvedShopId}`);
      
      if (response.data.ok) {
        // Remove from current list
        setClients(prev => prev.filter(client => client.id !== clientId));
        setTotalClients(prev => prev - 1);
        console.log('✅ Client deleted successfully!');
      }
    } catch (error) {
      console.error('Error deleting client:', error);
      setErrorMessage(t('admin.failed_to_delete_client'));
      setShowErrorModal(true);
    } finally {
      setActionLoading(prev => ({ ...prev, [clientId]: false }));
    }
  };

  const canEdit = user?.role === 'shop_admin' || user?.role === 'superadmin' || user?.role === 'barber';
  const canArchive = user?.role === 'shop_admin' || user?.role === 'superadmin' || user?.role === 'barber';

  // Select all functionality
  const handleSelectAll = () => {
    if (selectedClients.size === clients.length) {
      // Deselect all
      setSelectedClients(new Set());
    } else {
      // Select all visible clients
      setSelectedClients(new Set(clients.map(client => client.id)));
    }
  };

  const handleSelectClient = (clientId) => {
    const newSelected = new Set(selectedClients);
    if (newSelected.has(clientId)) {
      newSelected.delete(clientId);
    } else {
      newSelected.add(clientId);
    }
    setSelectedClients(newSelected);
  };

  const isAllSelected = clients.length > 0 && selectedClients.size === clients.length;
  const isPartiallySelected = selectedClients.size > 0 && selectedClients.size < clients.length;

  // Bulk actions
  const handleBulkArchive = async () => {
    if (selectedClients.size === 0) return;
    
    setBulkActionLoading(true);
    try {
      const archivePromises = Array.from(selectedClients).map(clientId => 
        axiosClient.patch(`/admin/clients/${clientId}/archive`)
      );
      
      await Promise.all(archivePromises);
      
      // Update UI
      if (statusFilter === 'active') {
        setClients(prev => prev.filter(client => !selectedClients.has(client.id)));
        setTotalClients(prev => prev - selectedClients.size);
      } else {
        loadClients(); // Reload to show updated status
      }
      
      setSelectedClients(new Set());
      console.log(`✅ Successfully archived ${selectedClients.size} clients`);
    } catch (error) {
      console.error('Error archiving clients:', error);
      setErrorMessage(t('admin.failed_to_archive_clients', { count: selectedClients.size }));
      setShowErrorModal(true);
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedClients.size === 0) return;
    
    const selectedClientData = clients.filter(client => selectedClients.has(client.id));
    setConfirmData({
      type: 'bulk_delete',
      title: t('admin.delete_multiple_clients'),
      message: t('admin.delete_multiple_clients_confirm', { count: selectedClients.size }),
      details: selectedClientData.slice(0, 5).map(client => 
        `• ${client.name} (${client.email || t('admin.no_email')})`
      ).concat(selectedClientData.length > 5 ? [t('admin.and_more', { count: selectedClientData.length - 5 })] : []),
      warning: t('admin.cannot_be_undone_warning'),
      onConfirm: () => performBulkDelete()
    });
    setShowConfirmModal(true);
  };

  const performBulkDelete = async () => {
    setBulkActionLoading(true);
    try {
      const urlShopId =
        typeof window !== 'undefined' ? new URLSearchParams(window.location.search).get('shopId') : null;
      const resolvedShopId = user?.shop_id || urlShopId || null;
      if (!resolvedShopId) {
        setErrorMessage(t('admin.missing_shop_id'));
        setShowErrorModal(true);
        return;
      }
      const deletePromises = Array.from(selectedClients).map(clientId => 
        axiosClient.delete(`/admin/clients/${clientId}?shop_id=${resolvedShopId}`)
      );
      
      await Promise.all(deletePromises);
      
      // Update UI
      setClients(prev => prev.filter(client => !selectedClients.has(client.id)));
      setTotalClients(prev => prev - selectedClients.size);
      setSelectedClients(new Set());
      
      console.log(`✅ Successfully deleted ${selectedClients.size} clients`);
    } catch (error) {
      console.error('Error deleting clients:', error);
      setErrorMessage(t('admin.failed_to_delete_clients', { count: selectedClients.size }));
      setShowErrorModal(true);
    } finally {
      setBulkActionLoading(false);
    }
  };

  // Clear selection when filters change
  useEffect(() => {
    setSelectedClients(new Set());
  }, [searchQuery, statusFilter, currentPage]);

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-4 md:mb-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 md:gap-4">
          <div className="flex-1 min-w-0">
            <h1 className="text-xl md:text-2xl font-bold flex items-center text-zinc-900 dark:text-zinc-100">
              <svg className="h-5 w-5 md:h-6 md:w-6 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a4 4 0 11-8 0 4 4 0 018 0z" />
              </svg>
              {t('admin.clients_page_title')}
            </h1>
            <p className="text-xs md:text-sm text-zinc-600 dark:text-zinc-400 mt-0.5 md:mt-1">
              {t('admin.clients_subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2 md:gap-3 flex-shrink-0">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowCreateModal(true)}
              className="px-3 md:px-4 py-2 rounded-xl text-white text-sm md:text-base font-medium flex items-center gap-1.5 md:gap-2 transition-all duration-200 hover:shadow-lg whitespace-nowrap"
              style={{ background: ACCENT }}
            >
              <Icons.Plus />
              <span className="hidden xs:inline">{t('admin.create_client')}</span>
              <span className="xs:hidden">{t('admin.create')}</span>
            </motion.button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-4 md:mb-6">
        <CardHeader
          title={t('admin.filters')}
          subtitle={t('admin.filter_clients_subtitle')}
          icon={
            <svg className="h-4 w-4 md:h-5 md:w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
            </svg>
          }
        />
        <div className="p-3 md:p-4 lg:p-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
            <div>
              <label className="block text-xs md:text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5 md:mb-2">
                {t('admin.col_status')}
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 md:px-4 py-2 md:py-3 text-sm md:text-base rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                style={{ '--accent': ACCENT }}
              >
                <option value="active">{t('admin.active_clients')}</option>
                <option value="archived">{t('admin.archived_clients')}</option>
                <option value="all">{t('admin.all_clients_filter')}</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs md:text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-1.5 md:mb-2">
                {t('admin.search_client')}
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-2 md:pl-3 flex items-center pointer-events-none">
                  <svg className="h-4 w-4 md:h-5 md:w-5 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                  </svg>
                </div>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder={t('admin.client_name_email_phone_placeholder')}
                  className="w-full pl-8 md:pl-10 pr-3 md:pr-4 py-2 md:py-3 text-sm md:text-base rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 focus:outline-none focus:ring-2 focus:ring-[--accent]/20 focus:border-[--accent]/30 transition-all"
                  style={{ '--accent': ACCENT }}
                />
              </div>
            </div>
          </div>
          
          {/* Bulk Actions */}
          {selectedClients.size > 0 && (
            <div className="mt-4 pt-4 border-t border-white/10 dark:border-white/10">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm text-zinc-600 dark:text-zinc-400">
                  {t('admin.selected_count', { count: selectedClients.size })}
                </span>
                {canArchive && statusFilter === 'active' && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleBulkArchive}
                    disabled={bulkActionLoading}
                    className="px-3 py-1 text-sm rounded-lg flex items-center text-white disabled:opacity-50 transition-all"
                    style={{ backgroundColor: ACCENT }}
                  >
                    {bulkActionLoading ? <Icons.Loading /> : <Icons.Archive />}
                    <span className="ml-1">{t('admin.archive_client')}</span>
                  </motion.button>
                )}
                {canEdit && (
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={handleBulkDelete}
                    disabled={bulkActionLoading}
                    className="px-3 py-1 text-sm rounded-lg flex items-center bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 transition-all"
                  >
                    {bulkActionLoading ? <Icons.Loading /> : <Icons.Delete />}
                    <span className="ml-1">{t('admin.delete')}</span>
                  </motion.button>
                )}
              </div>
            </div>
          )}
        </div>
      </Card>

      {/* Clients Table */}
      <Card>
        <CardHeader
          title={t('admin.clients_page_title')}
          subtitle={t('admin.total_clients', { count: totalClients })}
          icon={
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" style={{ color: ACCENT }}>
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          }
        />
        <div className="admin-table-container admin-table-container--mobile-scroll">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-zinc-500 dark:text-zinc-400 border-b border-white/10 dark:border-white/10">
                <th className="py-4 px-4 font-medium hidden md:table-cell">
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    ref={(input) => {
                      if (input) input.indeterminate = isPartiallySelected;
                    }}
                    onChange={handleSelectAll}
                    className="rounded border-2 bg-white/50 dark:bg-zinc-800/50 border-white/20 dark:border-white/10 text-blue-600 focus:ring-blue-500"
                    title={isAllSelected ? 'Deselect all' : 'Select all'}
                  />
                </th>
                <th className="py-4 px-4 font-medium">{t('admin.client_profile')}</th>
                <th className="py-4 px-4 font-medium hidden lg:table-cell">{t('admin.contact_label')}</th>
                <th className="py-4 px-4 font-medium hidden lg:table-cell">{t('admin.preferred_barber_label')}</th>
                <th className="py-4 px-4 font-medium">{t('admin.col_status')}</th>
                <th className="py-4 px-4 font-medium hidden xl:table-cell">{t('admin.created_at')}</th>
                <th className="py-4 px-4 font-medium text-right">{t('admin.actions')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10 dark:divide-white/10">
              {loading ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center">
                    <div className="flex items-center justify-center gap-3 text-zinc-600 dark:text-zinc-400">
                      <CircularProgress size={24} style={{ color: ACCENT }} />
                      <span>{t('admin.loading_clients')}</span>
                    </div>
                  </td>
                </tr>
              ) : clients.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center">
                    <div className="text-center">
                      <div className="h-12 w-12 mx-auto mb-4 rounded-xl bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                        <svg className="h-6 w-6 text-zinc-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a4 4 0 11-8 0 4 4 0 018 0z" />
                        </svg>
                      </div>
                      <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100 mb-2">
                        {t('admin.no_clients_found')}
                      </h3>
                      <p className="text-zinc-500 dark:text-zinc-400">
                        {canEdit ? t('admin.create_first_client') : t('admin.no_clients_match_filters')}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                clients.map((client) => (
                  <motion.tr 
                    key={client.id} 
                    id={`client-row-${client.id}`}
                    className={`text-zinc-800 dark:text-zinc-100 hover:bg-white/30 dark:hover:bg-zinc-800/30 transition-colors ${
                      focusClientId === client.id ? 'ring-2 ring-[#7d6633] ring-offset-2 ring-offset-white dark:ring-offset-zinc-900' : ''
                    }`}
                    whileHover={{ backgroundColor: 'rgba(255,255,255,0.1)' }}
                  >
                    <td className="py-4 px-4 hidden md:table-cell">
                      <input
                        type="checkbox"
                        checked={selectedClients.has(client.id)}
                        onChange={() => handleSelectClient(client.id)}
                        className="rounded border-2 bg-white/50 dark:bg-zinc-800/50 border-white/20 dark:border-white/10 text-blue-600 focus:ring-blue-500"
                        title={`Select ${client.name}`}
                      />
                    </td>
                    <td className="py-4 px-4">
                      <div className="flex items-center">
                        <ProfileAvatar
                          name={client.name}
                          avatarUrl={client.avatar_url}
                          size="sm"
                          previewable
                        />
                        <div className="ml-3 min-w-0 flex-1">
                          <div className="font-medium text-zinc-900 dark:text-zinc-100">
                            {client.name}
                          </div>
                          {/* Mobile: Show contact info stacked */}
                          <div className="mt-1 space-y-0.5 lg:hidden">
                            {client.email && (
                              <div className="text-xs text-zinc-600 dark:text-zinc-300">{client.email}</div>
                            )}
                            {client.phone && (
                              <div className="text-xs text-zinc-500 dark:text-zinc-400">{client.phone}</div>
                            )}
                            {client.preferred_barber && (
                              <div className="text-xs text-zinc-500 dark:text-zinc-400">
                                {t('admin.barber_label')}: {client.preferred_barber.name}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="py-4 px-4 hidden lg:table-cell">
                      <div>
                        {client.email && (
                          <div className="text-sm text-zinc-900 dark:text-zinc-100">{client.email}</div>
                        )}
                        {client.phone && (
                          <div className="text-sm text-zinc-500 dark:text-zinc-400">{client.phone}</div>
                        )}
                        {!client.email && !client.phone && (
                          <span className="text-sm text-zinc-500 dark:text-zinc-400">{t('admin.no_contact_info')}</span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4 hidden lg:table-cell">
                      <div className="text-zinc-800 dark:text-zinc-100">
                        {client.preferred_barber ? client.preferred_barber.name : (
                          <span className="text-zinc-500 dark:text-zinc-400">{t('admin.no_preference')}</span>
                        )}
                      </div>
                    </td>
                    <td className="py-4 px-4">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                        client.status === 'active' 
                          ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300' 
                          : 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300'
                      }`}>
                        {client.status === 'active' ? t('admin.status_active') : t('admin.archived_clients')}
                      </span>
                    </td>
                    <td className="py-4 px-4 hidden xl:table-cell">
                      <div className="text-zinc-800 dark:text-zinc-100">
                        {new Date(client.created_at).toLocaleDateString(i18n.language)}
                      </div>
                    </td>
                    <td className="py-4 px-4 text-right">
                      <div className="flex items-center gap-2 justify-end">
                        {canEdit && client.status === 'active' && (
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => {
                              setSelectedClient(client);
                              setShowEditModal(true);
                            }}
                            className="p-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
                            title={t('admin.edit_client')}
                          >
                            <Icons.Edit />
                          </motion.button>
                        )}

                        {/* AI Analysis Button */}
                        <motion.button
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                          onClick={() => {
                            navigate(`/admin/clients/${client.id}/ai-analysis`);
                          }}
                          className="p-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
                          title={t('admin.view_ai_analysis')}
                        >
                          <Icons.HairAnalysis />
                        </motion.button>
                        
                        {canArchive && client.status === 'active' && (
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleArchive(client.id)}
                            disabled={actionLoading[client.id]}
                            className="p-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all disabled:opacity-50"
                            title={t('admin.archive_client')}
                          >
                            {actionLoading[client.id] ? <Icons.Loading /> : <Icons.Archive />}
                          </motion.button>
                        )}
                        
                        {canArchive && client.status === 'archived' && (
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleRestore(client.id)}
                            disabled={actionLoading[client.id]}
                            className="p-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all disabled:opacity-50"
                            title={t('admin.restore_client')}
                          >
                            {actionLoading[client.id] ? <Icons.Loading /> : <Icons.Restore />}
                          </motion.button>
                        )}

                        {canEdit && (
                          <motion.button
                            whileHover={{ scale: 1.1 }}
                            whileTap={{ scale: 0.9 }}
                            onClick={() => handleDelete(client.id)}
                            disabled={actionLoading[client.id]}
                            className="p-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 transition-all disabled:opacity-50"
                            title={t('admin.delete_client')}
                          >
                            {actionLoading[client.id] ? <Icons.Loading /> : <Icons.Delete />}
                          </motion.button>
                        )}
                      </div>
                    </td>
                  </motion.tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <Card className="mt-6">
          <div className="p-4 md:p-5">
            <div className="flex items-center justify-between admin-pagination-wrapper">
              <div className="text-sm text-zinc-600 dark:text-zinc-400 admin-pagination-info">
                {t('admin.showing_results', { from: ((currentPage - 1) * pageSize) + 1, to: Math.min(currentPage * pageSize, totalClients), total: totalClients, entity: t('admin.clients_entity') })}
              </div>
              <div className="flex items-center gap-4 admin-pagination-controls">
                <div className="flex items-center gap-2 admin-pagination-buttons">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {t('admin.previous')}
                  </motion.button>
                  <span className="px-3 py-2 text-sm text-zinc-600 dark:text-zinc-400">
                    {t('admin.page_of', { page: currentPage, total: totalPages })}
                  </span>
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 rounded-lg border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-900 dark:text-zinc-100 hover:bg-white/70 dark:hover:bg-zinc-800/70 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                  >
                    {t('admin.next')}
                  </motion.button>
                </div>
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Modals */}
      <CreateClientModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSuccess={handleCreateSuccess}
        isDark={isDark}
        barbers={barbers}
        user={user}
      />

      <EditClientModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        onSuccess={handleEditSuccess}
        isDark={isDark}
        client={selectedClient}
        barbers={barbers}
        user={user}
      />

      {/* Confirmation Modal */}
      {showConfirmModal && confirmData && (
        <PortalModal open={showConfirmModal}>
          <div className="hc-modal-overlay bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center">
            <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="hc-modal-panel p-4 md:p-6 rounded-2xl shadow-2xl max-w-md w-full my-8 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border border-white/20 dark:border-white/10"
          >
            <div className="flex items-center mb-4">
              <div className="h-10 w-10 rounded-xl bg-orange-100 dark:bg-orange-900/30 flex items-center justify-center mr-3">
                <svg className="h-5 w-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">
                {confirmData.title}
              </h3>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-zinc-600 dark:text-zinc-400 mb-3">
                {confirmData.message}
              </p>
              {confirmData.details && (
                <div className="bg-zinc-100 dark:bg-zinc-800/50 p-4 rounded-xl text-sm border border-white/10 dark:border-white/10">
                  {confirmData.details.map((detail, index) => (
                    <div key={index} className="text-zinc-700 dark:text-zinc-300">{detail}</div>
                  ))}
                </div>
              )}
              {confirmData.warning && (
                <p className="text-sm text-red-600 dark:text-red-400 mt-3 font-medium">
                  {confirmData.warning}
                </p>
              )}
            </div>
            
            <div className="flex justify-end gap-3">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowConfirmModal(false)}
                className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
              >
                {t('admin.cancel')}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => {
                  setShowConfirmModal(false);
                  confirmData.onConfirm();
                }}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all shadow-lg"
              >
                {t('admin.confirm')}
              </motion.button>
            </div>
            </motion.div>
          </div>
        </PortalModal>
      )}

      {/* Error Modal */}
      {showErrorModal && (
        <PortalModal open={showErrorModal}>
          <div className="hc-modal-overlay bg-black/50 backdrop-blur-sm flex items-start sm:items-center justify-center">
            <motion.div 
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="hc-modal-panel p-4 md:p-6 rounded-2xl shadow-2xl max-w-md w-full my-8 bg-white/90 dark:bg-zinc-900/90 backdrop-blur-xl border border-white/20 dark:border-white/10"
          >
            <div className="flex items-center mb-4">
              <div className="h-10 w-10 rounded-xl bg-red-100 dark:bg-red-900/30 flex items-center justify-center mr-3">
                <svg className="h-5 w-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-zinc-900 dark:text-zinc-100">
                {t('admin.error')}
              </h3>
            </div>
            
            <div className="mb-6">
              <p className="text-sm text-zinc-600 dark:text-zinc-400">
                {errorMessage}
              </p>
            </div>
            
            <div className="flex justify-end">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowErrorModal(false)}
                className="px-4 py-2 rounded-xl border border-white/20 dark:border-white/10 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm text-zinc-700 dark:text-zinc-300 hover:bg-white/70 dark:hover:bg-zinc-800/70 transition-all"
              >
                {t('admin.ok')}
              </motion.button>
            </div>
            </motion.div>
          </div>
        </PortalModal>
      )}
    </div>
  );
};

export default Clients;